/**
 * Full-text search over help articles, built for how readers actually type.
 *
 * It replaced Fuse.js, which matched characters rather than words: it did not
 * know "batal" from "cancel" or "pembatalan" from "batal", it scored a word by
 * its distance from the start of the article (so most of a body was invisible),
 * and it treated "batal pre order" as one 15-letter pattern. "batal pre order"
 * did not find "Kalau sudah Pre-Order, boleh cancel nggak?"; "telat bayar" did
 * not find "telat lunasin".
 *
 * What it does instead:
 * - words, not characters, with a light Indonesian stemmer, so "pembatalan",
 *   "dibatalkan" and "batal" meet;
 * - synonyms and stopwords from data/searchVocabulary.js;
 * - BM25 ranking with field weights (title > section labels > category > body),
 *   ordered first by how many of the reader's words an article answers;
 * - typo tolerance (edit distance 1-2) and prefix matching for type-ahead;
 * - a snippet around the best-matching sentence, with the matched words marked.
 *
 * Dependency-free and pure, so it is tested directly (lib/__tests__).
 */
import vocabulary from "../data/searchVocabulary";

const SYNONYM_GROUPS = vocabulary.synonyms;
const STOPWORDS = vocabulary.stopwords;

const FIELD_WEIGHTS = { title: 3, labels: 2, category: 1.5, body: 1 };
const K1 = 1.2;
const B = 0.75;
const MIN_COVERAGE = 0.5;
/** Results scoring under this share of the best one are dropped as noise. */
const MIN_RELATIVE_SCORE = 0.15;

const STOP = new Set(STOPWORDS);

/* ---- text ----------------------------------------------------------------- */

/** Markdown/HTML to plain text: link text kept, URLs, images and tags dropped. */
export function plainText(markdown) {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/<[^>]+>/g, " ")
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/-{3,}/g, " ")
    .replace(/[*_`~#>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Lower-case words. A hyphenated word yields itself joined and its parts
 * ("pre-order" -> "preorder", "pre", "order"), so either spelling matches.
 */
export function words(text) {
  const out = [];
  const raw = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .match(/[a-z0-9]+(?:-[a-z0-9]+)*/g);

  (raw || []).forEach(word => {
    if (word.indexOf("-") === -1) {
      out.push(word);
      return;
    }
    out.push(word.replace(/-/g, ""));
    word.split("-").forEach(part => out.push(part));
  });

  return out.filter(word => word.length >= 2 || /\d/.test(word));
}

/* ---- Indonesian stemming ------------------------------------------------- */

const VOWEL = /^[aiueo]/;

/**
 * Possible roots of a word, the word itself first. Deliberately light: it
 * strips particles (-lah, -kah, -pun), possessives (-nya, -ku, -mu), suffixes
 * (-kan, -an, -i) and prefixes (di-, ke-, se-, ter-, ber-, me-/pe- with their
 * sound changes), keeping every candidate rather than guessing one -- meng- +
 * "irim" could be "kirim" or "irim", so both are indexed. Roots shorter than
 * three letters are discarded.
 */
export function stems(word) {
  const found = [word];
  const add = candidate => {
    if (candidate.length >= 3 && found.indexOf(candidate) === -1) found.push(candidate);
  };

  let base = word;
  [/(lah|kah|tah|pun)$/, /(nya|ku|mu)$/].forEach(pattern => {
    const stripped = base.replace(pattern, "");
    if (stripped !== base && stripped.length >= 4) {
      base = stripped;
      add(base);
    }
  });

  const bases = [base];
  ["kan", "an", "i"].forEach(suffix => {
    if (base.length - suffix.length >= 4 && base.slice(-suffix.length) === suffix) {
      bases.push(base.slice(0, -suffix.length));
    }
  });

  bases.forEach(b => {
    add(b);
    stripPrefixes(b).forEach(add);
  });

  return found;
}

function stripPrefixes(word) {
  const out = [];
  const rest = n => word.slice(n);

  if (/^(di|ke|se)/.test(word) && word.length >= 6) out.push(rest(2));
  if (/^(ter|ber)/.test(word) && word.length >= 6) out.push(rest(3));
  if (/^be[^r]/.test(word) && word.length >= 6) out.push(rest(2));

  const nasal = word.match(/^(me|pe)(ng|ny|m|n)?(.*)$/);
  if (nasal && word.length >= 5) {
    const [, , nasalPart, tail] = nasal;
    if (nasalPart === "ng") {
      out.push(tail);
      if (VOWEL.test(tail)) out.push(`k${tail}`);
    } else if (nasalPart === "ny" && VOWEL.test(tail)) {
      out.push(`s${tail}`);
    } else if (nasalPart === "m") {
      if (VOWEL.test(tail)) out.push(`p${tail}`);
      else if (/^[bfv]/.test(tail)) out.push(tail);
    } else if (nasalPart === "n") {
      if (VOWEL.test(tail)) out.push(`t${tail}`);
      else if (/^[cdjz]/.test(tail)) out.push(tail);
    } else if (nasalPart === undefined && /^[lrwymn]/.test(tail)) {
      out.push(tail);
    }
  }

  return out.filter(candidate => candidate.length >= 3);
}

/* ---- synonyms ------------------------------------------------------------- */

const PHRASES = SYNONYM_GROUPS.map(group => group.map(entry => words(entry)));

/**
 * Splits query words into concepts, longest synonym phrase first. Each concept
 * lists the word sequences that count as a match for it: what the reader typed,
 * and every synonym of it.
 */
/** The longest synonym phrase starting at `queryWords[start]`, or null. */
function phraseAt(queryWords, start) {
  let matched = null;

  PHRASES.forEach(group => {
    group.forEach(phrase => {
      const fits = phrase.every((w, k) => queryWords[start + k] === w);
      if (fits && (matched === null || phrase.length > matched.phrase.length)) matched = { phrase, group };
    });
  });

  return matched;
}

export function concepts(queryWords) {
  const out = [];
  let i = 0;

  while (i < queryWords.length) {
    const matched = phraseAt(queryWords, i);

    if (matched !== null) {
      // "pre-order" arrives as "preorder", "pre", "order": two spellings of
      // one synonym group, which should count as one thing asked.
      if (!out.some(concept => concept.alternatives === matched.group)) {
        out.push({ text: matched.phrase.join(" "), alternatives: matched.group });
      }
      i += matched.phrase.length;
    } else {
      out.push({ text: queryWords[i], alternatives: [[queryWords[i]]] });
      i += 1;
    }
  }

  return out;
}

/* ---- edit distance (typos) ------------------------------------------------ */

/** Damerau-Levenshtein with transpositions, giving up beyond `max`. */
export function editDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;

  const rows = [];
  for (let i = 0; i <= a.length; i += 1) {
    rows.push([i]);
    for (let j = 1; j <= b.length; j += 1) rows[i][j] = i === 0 ? j : 0;
  }

  for (let i = 1; i <= a.length; i += 1) {
    let best = max + 1;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) value = Math.min(value, rows[i - 2][j - 2] + 1);
      rows[i][j] = value;
      best = Math.min(best, value);
    }
    if (best > max) return max + 1;
  }

  return rows[a.length][b.length];
}

function typoBudget(word) {
  if (word.length >= 8) return 2;
  if (word.length >= 4) return 1;
  return 0;
}

/* ---- index ---------------------------------------------------------------- */

/**
 * The closing line most articles share -- "Apabila kamu masih mempunyai
 * pertanyaan seputar refund, klik pertanyaan di bagian ... lainnya ya!" (82 of
 * 165). It is navigation, not an answer: indexed, it made every article in a
 * category match its category's name, and it kept winning the snippet.
 */
const CLOSING_LINE = /^\s*Apabila (kamu masih (mempunyai|punya)|ada) pertanyaan.*$/gim;

function answerText(markdown) {
  return markdown.replace(CLOSING_LINE, "");
}

/** Standalone bold lines and headings: the section labels of an article. */
function labelsOf(markdown) {
  return markdown
    .split("\n")
    .map(line => line.trim())
    .filter(line => /^#{1,6}\s/.test(line) || /^\*\*[^*]+\*\*\s*$/.test(line))
    .join(" ");
}

function termCounts(text) {
  const counts = {};
  let length = 0;

  words(text).forEach(word => {
    length += 1;
    stems(word).forEach(stem => {
      counts[stem] = (counts[stem] || 0) + 1;
    });
  });

  return { counts, length };
}

/**
 * Builds the index once. `articles`: [{ id, title, content, category: { slug,
 * title } }], as data/faqs/index.js produces them.
 */
export function createSearchIndex(articles) {
  const fields = Object.keys(FIELD_WEIGHTS);
  const docs = articles.map(article => {
    // One line per markdown line: a bold label ends its own "sentence"
    // instead of running into the paragraph under it in a snippet.
    const body = answerText(article.content)
      .split("\n")
      .map(plainText)
      .filter(Boolean)
      .join("\n");
    const sources = {
      title: article.title,
      labels: plainText(labelsOf(article.content)),
      category: article.category.title,
      body
    };
    const doc = { article, body, fields: {} };
    fields.forEach(field => {
      doc.fields[field] = termCounts(sources[field]);
    });
    return doc;
  });

  const df = {};
  const avgLength = {};
  fields.forEach(field => {
    avgLength[field] = docs.reduce((sum, doc) => sum + doc.fields[field].length, 0) / Math.max(docs.length, 1);
  });
  docs.forEach(doc => {
    const seen = {};
    fields.forEach(field => {
      Object.keys(doc.fields[field].counts).forEach(term => {
        seen[term] = true;
      });
    });
    Object.keys(seen).forEach(term => {
      df[term] = (df[term] || 0) + 1;
    });
  });

  const vocabulary = Object.keys(df);
  const N = docs.length;
  const idf = term => Math.log(1 + (N - df[term] + 0.5) / (df[term] + 0.5));

  /**
   * Index terms a query word stands for, each with a weight: its own stems at
   * full weight; failing those, near spellings (typos) and -- for the word
   * being typed -- completions, at a discount.
   */
  function expand(word, isLast, prefix) {
    const exact = stems(word).filter(stem => df[stem]);
    const out = exact.map(term => ({ term, weight: 1 }));

    if (prefix && isLast && word.length >= 2) {
      vocabulary
        .filter(term => term.length > word.length && term.indexOf(word) === 0)
        .sort((x, y) => df[y] - df[x])
        .slice(0, 12)
        .forEach(term => out.push({ term, weight: 0.8 }));
    }

    if (exact.length === 0) {
      const max = typoBudget(word);
      if (max > 0) {
        vocabulary.forEach(term => {
          if (term[0] === word[0] && editDistance(word, term, max) <= max) out.push({ term, weight: 0.7 });
        });
      }
    }

    return out;
  }

  function bm25(doc, term) {
    return fields.reduce((sum, field) => {
      const { counts, length } = doc.fields[field];
      const tf = counts[term];
      if (!tf) return sum;

      const norm = tf + K1 * (1 - B + (B * length) / (avgLength[field] || 1));
      return sum + FIELD_WEIGHTS[field] * idf(term) * ((tf * (K1 + 1)) / norm);
    }, 0);
  }

  /**
   * Ranked results: [{ article, score, snippet }]. `options.category` keeps one
   * category; `options.limit` caps the list; `options.prefix` treats the last
   * word as unfinished (type-ahead).
   */
  function search(query, options = {}) {
    const typed = words(query || "");
    if (typed.length === 0) return [];

    const meaningful = typed.filter(word => !STOP.has(word));
    const queryConcepts = concepts(meaningful.length > 0 ? meaningful : typed);
    const lastWord = typed[typed.length - 1];

    const plans = queryConcepts.map(concept => ({
      concept,
      alternatives: concept.alternatives.map(sequence =>
        sequence.map(word => expand(word, word === lastWord && sequence[sequence.length - 1] === word, options.prefix))
      )
    }));

    const phrase = meaningful.join(" ");
    const results = [];

    docs.forEach(doc => {
      if (options.category && doc.article.category.slug !== options.category) return;

      let score = 0;
      let covered = 0;
      const hitTerms = [];

      plans.forEach(plan => {
        let best = 0;
        let bestTerms = null;

        plan.alternatives.forEach(sequence => {
          let sequenceScore = 0;
          const terms = [];
          const complete = sequence.every(candidates => {
            let wordBest = 0;
            let wordTerm = null;
            candidates.forEach(({ term, weight }) => {
              const value = bm25(doc, term) * weight;
              if (value > wordBest) {
                wordBest = value;
                wordTerm = term;
              }
            });
            sequenceScore += wordBest;
            if (wordTerm) terms.push(wordTerm);
            return wordBest > 0;
          });

          if (complete && sequenceScore > best) {
            best = sequenceScore;
            bestTerms = terms;
          }
        });

        if (best > 0) {
          covered += 1;
          score += best;
          bestTerms.forEach(term => hitTerms.push(term));
        }
      });

      const coverage = covered / plans.length;
      if (covered === 0 || coverage < MIN_COVERAGE) return;

      // A title that contains the reader's words in order is almost always
      // the article they mean.
      if (meaningful.length >= 2 && words(doc.article.title).join(" ").indexOf(phrase) !== -1) score *= 1.5;

      results.push({ doc, coverage, score, hitTerms });
    });

    results.sort((x, y) => y.coverage - x.coverage || y.score - x.score);

    /*
     * When some articles answer everything that was asked, the ones that only
     * answer part of it are noise: for "batal pre order", every article that
     * merely mentions Pre-Order. Up to two concepts, require all of them; from
     * three on, allow one to be missing. Then drop the long tail.
     */
    const top = results[0];
    const required = plans.length <= 2 ? 1 : (plans.length - 1) / plans.length;
    const kept = results.filter(result => {
      if (top && top.coverage >= required && result.coverage < required) return false;
      return !top || result.score >= top.score * MIN_RELATIVE_SCORE;
    });

    const limited = options.limit ? kept.slice(0, options.limit) : kept;
    return limited.map(result => ({
      article: result.doc.article,
      score: result.score,
      snippet: snippet(result.doc.body, result.hitTerms)
    }));
  }

  return { search };
}

/* ---- snippets ------------------------------------------------------------- */

const SNIPPET_LENGTH = 170;

/** True when this surface word is one of the matched index terms. */
function isHit(word, hitTerms) {
  return stems(word.toLowerCase()).some(stem => hitTerms.indexOf(stem) !== -1);
}

/**
 * The sentence with the most matched words, trimmed to about 170 characters
 * around the first of them, as [{ text, hit }] so the UI can mark the hits
 * without rendering HTML.
 */
function snippet(body, hitTerms) {
  if (!body) return [];

  // Distinct matched words decide, not repeats: a sentence saying "refund"
  // three times is not a better answer than one with "refund" and "lama".
  // A full sentence beats a short bold label with as many hits -- the label
  // names a section, the sentence answers. Otherwise the earlier one wins.
  const sentences = body.match(/[^.!?\n]+[.!?]*/g) || [body];
  let best = sentences[0];
  let bestScore = -1;
  sentences.forEach(sentence => {
    const sentenceWords = sentence.match(/[A-Za-z0-9]+/g) || [];
    const distinct = {};
    sentenceWords.forEach(word => {
      if (isHit(word, hitTerms)) distinct[word.toLowerCase()] = true;
    });
    const score = Object.keys(distinct).length * 2 + (sentenceWords.length >= 8 ? 1 : 0);
    if (score > bestScore) {
      best = sentence;
      bestScore = score;
    }
  });

  let text = best.trim();
  if (text.length > SNIPPET_LENGTH) {
    const firstHit = (text.match(/[A-Za-z0-9]+/g) || []).find(word => isHit(word, hitTerms));
    const at = firstHit ? Math.max(0, text.indexOf(firstHit) - 40) : 0;
    const start = at > 0 ? text.indexOf(" ", at) + 1 : 0;
    text = `${start > 0 ? "… " : ""}${text.slice(start, start + SNIPPET_LENGTH).trim()}…`;
  }

  return text.split(/([A-Za-z0-9]+)/).filter(Boolean).reduce((parts, piece) => {
    const hit = /[A-Za-z0-9]/.test(piece) && isHit(piece, hitTerms);
    const last = parts[parts.length - 1];
    if (last && last.hit === hit) last.text += piece;
    else parts.push({ text: piece, hit });
    return parts;
  }, []);
}
